import {User} from "../model/user/User";
import {hasAdminGroup, hasAssistantGroup, hasExerciseReviewerGroup} from "./AuthUtil";

// Central deadline for product access - September 26, 2025 at 18:00 Warsaw Time
export const PRODUCT_ACCESS_DEADLINE = new Date('2025-09-26T18:00:00+02:00'); // Warsaw is UTC+2
const CUTOFF_DATE = new Date('2025-09-14T18:00:00+02:00'); // September 14, 2025 at 18:00 Warsaw time

/**
 * Checks if products should be locked for a given user
 * This was used for the initial launch period (before Sept 26, 2025)
 * Now replaced by shouldShowLockedTools for permanent restrictions
 *
 * @deprecated Use shouldShowLockedTools instead for new users without access
 */
export function shouldLockProducts(user: User): boolean {
    // This function is now only used for backward compatibility
    // The deadline has passed, so this returns false for everyone
    return false;
}

/**
 * Checks if user should be shown locked tools (permanent restriction)
 * This applies indefinitely to users who:
 * 1. Joined after September 14, 2025 at 18:00
 * 2. Don't have KED access
 * 3. Don't have an active SaaS subscription
 *
 * This restriction continues indefinitely until manually changed
 */
export function shouldShowLockedTools(user: User): boolean {
    return false;
    // Admin, Assistant, and Exercise_Reviewer groups never see locked tools
    const groups = user.groups || [];
    if (hasAdminGroup(groups) || hasAssistantGroup(groups) || hasExerciseReviewerGroup(groups)) {
        return false;
    }

    // Users with active SaaS subscription or KED access don't see locked tools
    if (user.saasSubscription?.isActive || user.hasAccessToKED) {
        return false;
    }

    // Check if user joined after September 14, 2025 at 18:00
    const joinedAfterCutoff = user.createdAt && new Date(user.createdAt) > CUTOFF_DATE;

    return !!joinedAfterCutoff;
}

/**
 * Gets the unlock redirect path based on user type
 */
export function getUnlockRedirectPath(user: User): string {
    return user.isParent ? '/platforma-rodzic' : '/platforma';
}

/**
 * Checks if a specific tool should show as locked with "Odblokuj teraz" button
 * Only applies to: planer nauki, plan dominacji, fiszki, zadania
 * For users who joined after Sept 14, 2025 without KED or active SaaS
 */
export function shouldShowToolAsLocked(user: User, toolName: 'planer' | 'dominacja' | 'fiszki' | 'zadania'): boolean {
    return false;
    // Admin, Assistant, and Exercise_Reviewer groups never see locked tools
    const groups = user.groups || [];
    if (hasAdminGroup(groups) || hasAssistantGroup(groups) || hasExerciseReviewerGroup(groups)) {
        return false;
    }

    // Check if user meets the lock criteria
    if (shouldShowLockedTools(user)) {
        // Only these specific tools are locked
        const lockedTools = ['planer', 'dominacja', 'fiszki', 'zadania'];
        return lockedTools.includes(toolName);
    }

    return false;
}

/**
 * Gets the deadline date for display purposes
 */
export function getProductAccessDeadline(): Date {
    return PRODUCT_ACCESS_DEADLINE;
}

/**
 * Checks if a specific product should be accessible
 * Currently applies to: planer nauki, plan dominacji, fiszki, profil edukacyjny, zadania, panel-rodzica
 *
 * Access rules:
 * 1. Admin/Assistant/Exercise_Reviewer groups: Always have access
 * 2. Users who joined after Sept 14, 2025 without KED/SaaS:
 *    - LOCKED: planer nauki, plan dominacji, fiszki, zadania
 *    - ACCESSIBLE: AI tools (not handled here), profil-edukacyjny, panel-rodzica
 * 3. All other users: Have full access
 */
export function hasAccessToProduct(user: User, productName: 'planer' | 'dominacja' | 'fiszki' | 'profil-edukacyjny' | 'zadania' | 'panel-rodzica'): boolean {
    return true;
    // Admin, Assistant, and Exercise_Reviewer groups always have access to all products
    const groups = user.groups || [];
    if (hasAdminGroup(groups) || hasAssistantGroup(groups) || hasExerciseReviewerGroup(groups)) {
        return true;
    }

    // Check if this user should have tools locked
    // (joined after Sept 14, 2025 AND no KED AND no active SaaS)
    if (shouldShowLockedTools(user)) {
        // These specific tools are locked for these users
        const lockedTools = ['planer', 'dominacja', 'fiszki', 'zadania'];
        if (lockedTools.includes(productName)) {
            return false; // Tool is locked
        }
        // profil-edukacyjny and panel-rodzica remain accessible
    }

    // Zadania and Panel Rodzica were time-locked until Sept 26, 2025
    // Now they're available to everyone (except those caught by shouldShowLockedTools above)
    if (productName === 'zadania' || productName === 'panel-rodzica') {
        const now = new Date();
        return now >= PRODUCT_ACCESS_DEADLINE;
    }

    // All other users have full access
    return true;
}

